import React, { useState, useEffect } from "react";
import { GiCrossedSwords, GiShield } from "react-icons/gi";
import { FaEye, FaHandsHelping, FaArrowLeft, FaChartBar, FaBolt, FaChevronDown, FaChevronRight } from "react-icons/fa";
import ChartImpact from "./ChartImpact";

/* ── Palette identique à ScoutPage ─────────────────────────────────── */
const C = {
  bg:       "#09090b",
  surface:  "#18181b",
  surface2: "#27272a",
  border:   "#27272a",
  borderSub:"#3f3f46",
  text:     "#e4e4e7",
  muted:    "#71717a",
  accent:   "#3b82f6",
  accentH:  "#60a5fa",
  win:      "#4ade80",
  loss:     "#f87171",
  gold:     "#eab308",
};

/* ── Constantes impact ──────────────────────────────────────────────── */
const CATEGORIZED_STATS = {
  "Dégâts": ["damageDealtToBuildings","damageDealtToObjectives","damageDealtToTurrets","totalDamageDealt","totalDamageDealtToChampions","physicalDamageDealtToChampions","magicDamageDealtToChampions","trueDamageDealtToChampions"],
  "Tankiness": ["totalDamageTaken","damageSelfMitigated"],
  "Soins et Boucliers": ["effectiveHealAndShielding","totalDamageShieldedOnTeammates","totalHealsOnTeammates","totalHeal"],
  "Contrôle": ["enemyChampionImmobilizations","timeCCingOthers","totalTimeCCDealt"],
  "Kills / Participation": ["deaths","soloKills","killParticipation","pickKillWithAlly","immobilizeAndKillWithAlly","killAfterHiddenWithAlly"],
  "Économie / CS": ["goldEarned","totalMinionsKilled","totalAllyJungleMinionsKilled","totalEnemyJungleMinionsKilled","turretKills"],
  "Early impact": ["earlyTakedowns","earlyCs10","earlyCsLead","earlyLevelLead","earlyPlatesTaken","killsOnLanersEarlyJungleAsJungler","killsOnOtherLanesEarlyJungleAsLaner"],
  "Skillshots": ["skillshotsDodged","skillshotsHit"],
  "Vision": ["controlWardsPlaced","wardsGuarded","wardsKilled","wardsPlaced"],
};

const STAT_LABELS = {
  controlWardsPlaced:"Balises de contrôle posées", damageDealtToBuildings:"Dégâts aux bâtiments",
  damageDealtToObjectives:"Dégâts aux objectifs", damageDealtToTurrets:"Dégâts aux tourelles",
  damageSelfMitigated:"Dégâts auto-mitigés", deaths:"Morts",
  effectiveHealAndShielding:"Soins et boucliers", enemyChampionImmobilizations:"Immobilisations",
  goldEarned:"Or gagné", killParticipation:"Participation aux kills",
  pickKillWithAlly:"Kill ciblé avec allié", skillshotsDodged:"Skillshots esquivés",
  skillshotsHit:"Skillshots touchés", soloKills:"Kills solo",
  timeCCingOthers:"Temps CC infligé", totalDamageDealt:"Dégâts totaux",
  totalDamageDealtToChampions:"Dégâts aux champions", totalDamageTaken:"Dégâts subis",
  totalHealsOnTeammates:"Soins aux alliés", totalMinionsKilled:"Sbires tués",
  wardsKilled:"Balises détruites", wardsPlaced:"Balises posées",
  earlyTakedowns:"Kills/assists early", earlyCs10:"CS a 10 min",
  earlyCsLead:"Avance CS max", earlyLevelLead:"Avance niveau max",
  earlyPlatesTaken:"Plaques prises",
  killsOnLanersEarlyJungleAsJungler:"Ganks early du jungler",
  killsOnOtherLanesEarlyJungleAsLaner:"Kills early hors lane",
};

const STAT_SECTIONS = [
  { id:"damage",  label:"Dégâts",      icon:GiCrossedSwords, description:"Forte propension à infliger des dégâts.",             match:(k)=>/damage|kill/i.test(k) },
  { id:"vision",  label:"Vision",      icon:FaEye,           description:"Tendance à prioriser le contrôle de la carte.",        match:(k)=>/vision|ward/i.test(k) },
  { id:"tanking", label:"Tanking",     icon:GiShield,        description:"Style orienté encaissement et mitigation.",            match:(k)=>/tank|mitig/i.test(k)  },
  { id:"utility", label:"Utilité/CC",  icon:FaHandsHelping,  description:"Profil orienté soutien, soins ou contrôles de foule.", match:(k)=>/cc|control|heal|shield/i.test(k) },
  { id:"early",   label:"Early impact", icon:FaBolt,         description:"Avantage cree dans les premieres minutes.",            match:(k)=>/^early/i.test(k) },
];

const SUMMONER_SPELLS = {
  1: "SummonerBoost",
  3: "SummonerExhaust",
  4: "SummonerFlash",
  6: "SummonerHaste",
  7: "SummonerHeal",
  11: "SummonerSmite",
  12: "SummonerTeleport",
  13: "SummonerMana",
  14: "SummonerDot",
  21: "SummonerBarrier",
  32: "SummonerSnowball",
};

const RUNE_LABELS = {
  8000: "Precision",
  8100: "Domination",
  8200: "Sorcellerie",
  8300: "Inspiration",
  8400: "Volonte",
};

function computeMainSection(impact) {
  if (!impact) return null;
  const scores = STAT_SECTIONS.map(s => {
    let sum = 0;
    for (const [k, v] of Object.entries(impact)) { const n = Number(v); if (Number.isFinite(n) && s.match(k)) sum += n; }
    return { section: s, score: sum };
  });
  const best = scores.reduce((a, b) => b.score > a.score ? b : a, scores[0]);
  return (!best || Math.abs(best.score) < 0.01) ? null : best;
}

/* ── Composant principal ────────────────────────────────────────────── */
export default function ScoutDetailModal({ player, onClose, ddVersion = "14.21.1", side = "ally" }) {
  const [activeTab, setActiveTab]               = useState("impact");
  const [showDetailedImpact, setShowDetailedImpact] = useState(false);
  const [selectedChampion, setSelectedChampion] = useState(null);
  const [selectedStats, setSelectedStats]       = useState([]);
  const [expandedCats, setExpandedCats]         = useState({});
  const [sortDesc, setSortDesc]                 = useState(true);
  const [leftPanelTab, setLeftPanelTab]         = useState("champions");
  const [expandedGame, setExpandedGame]         = useState(null);

  useEffect(() => {
    if (player?.impact) {
      setSelectedChampion(null);
      setSelectedStats(Object.keys(player.impact));
      setExpandedCats(Object.keys(CATEGORIZED_STATS).reduce((a,c)=>({...a,[c]:false}),{}));
    }
  }, [player]);

  if (!player) return null;

  const isAlly = side === "ally";
  const cardBorder    = isAlly ? "1px solid rgba(74,222,128,0.45)" : "1px solid rgba(248,113,113,0.45)";
  const cardBorderTop = isAlly ? "2px solid rgba(74,222,128,0.8)" : "2px solid rgba(248,113,113,0.8)";
  const cardBg        = isAlly
    ? "linear-gradient(180deg, #111a12 0%, #09090b 100%)"
    : "linear-gradient(180deg, #1a1111 0%, #09090b 100%)";
  const innerBorder   = isAlly ? "1px solid rgba(74,222,128,0.15)" : "1px solid rgba(248,113,113,0.15)";
  const accentColor   = isAlly ? C.win : C.loss;
  const teamRgb       = isAlly ? "74,222,128" : "248,113,113";
  const cardSurface   = "#0c0c0e";
  const panelSurface  = "rgba(12,12,14,0.92)";
  const selectedTint  = `rgba(${teamRgb},0.12)`;

  const champData       = selectedChampion && player.champions?.find(c => c.champion === selectedChampion);
  const currentImpact   = champData?.impact ?? player.impact ?? {};
  const mainSection     = computeMainSection(currentImpact);
  const topImpactStats  = Object.entries(currentImpact).sort((a,b)=>b[1]-a[1]).slice(0,5);
  const filteredData    = Object.entries(currentImpact)
    .map(([stat,value])=>({stat,value}))
    .filter(d=>selectedStats.includes(d.stat))
    .sort((a,b)=>sortDesc ? (Number(b.value)||0)-(Number(a.value)||0) : (Number(a.value)||0)-(Number(b.value)||0));

  const toggleStat     = (s) => setSelectedStats(p=>p.includes(s)?p.filter(x=>x!==s):[...p,s]);
  const toggleCat      = (c) => setExpandedCats(p=>({...p,[c]:!p[c]}));
  const toggleCatAll   = (stats) => {
    const allOn = stats.every(s=>selectedStats.includes(s));
    setSelectedStats(p=>allOn?p.filter(s=>!stats.includes(s)):[...new Set([...p,...stats])]);
  };

  /* ── Calculs historique ── */
  const games  = player.games || [];
  const wins   = games.filter(g=>g.win).length;
  const losses = games.length - wins;
  const wr     = games.length ? Math.round(wins/games.length*100) : 0;
  const avgKda = games.length
    ? (games.reduce((s,g)=>(s+(g.kills+g.assists)/Math.max(g.deaths,1)),0)/games.length).toFixed(2)
    : "—";
  const totalCs = games.reduce((s,g)=>s+(g.cs_total||0),0);

  const kdaColor = (kda) => kda >= 3 ? C.win : kda >= 1.5 ? C.gold : C.loss;
  const formatDuration = (seconds) => {
    const total = Number(seconds) || 0;
    const min = Math.floor(total / 60);
    const sec = String(total % 60).padStart(2, "0");
    return `${min}:${sec}`;
  };
  const formatGold = (gold) => `${((Number(gold) || 0) / 1000).toFixed(1)}k`;
  const itemSrc = (itemId) => `https://ddragon.leagueoflegends.com/cdn/${ddVersion}/img/item/${itemId}.png`;
  const spellSrc = (spellId) => {
    const spell = SUMMONER_SPELLS[Number(spellId)];
    return spell ? `https://ddragon.leagueoflegends.com/cdn/${ddVersion}/img/spell/${spell}.png` : null;
  };
  const SmallIcon = ({ src, title, size = 22 }) => (
    src ? (
      <img
        src={src}
        alt={title || ""}
        title={title}
        style={{ width:size, height:size, borderRadius:5, objectFit:"cover", background:C.bg, border:`1px solid ${C.border}` }}
      />
    ) : (
      <span style={{ width:size, height:size, borderRadius:5, background:C.surface2, border:`1px solid ${C.border}`, display:"inline-block" }} />
    )
  );
  const StatPill = ({ label, value, color = C.text }) => (
    <div style={{ minWidth:0, background:cardSurface, border:`1px solid ${C.border}`, borderRadius:8, padding:"7px 9px" }}>
      <div style={{ fontSize:9, color:C.muted, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap" }}>{label}</div>
      <div style={{ fontSize:13, color, fontWeight:800, marginTop:2, whiteSpace:"nowrap" }}>{value}</div>
    </div>
  );

  /* ── Sous-composant filtre champion ── */
  const ChampionFilter = ({ height }) => (
    <div style={{ background: panelSurface, border: innerBorder, borderRadius: 10, padding: 14, display:"flex", flexDirection:"column", height: height||"100%" }}>
      <div style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10, flexShrink:0 }}>
        Filtrer par champion
      </div>
      <button
        onClick={()=>setSelectedChampion(null)}
        style={{ width:"100%", marginBottom:8, padding:"7px 12px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", flexShrink:0,
          background: selectedChampion===null ? selectedTint : "transparent",
          border: selectedChampion===null ? `1px solid rgba(${teamRgb},0.45)` : `1px solid ${C.borderSub}`,
          color: selectedChampion===null ? accentColor : C.muted,
        }}
      >
        Tous les champions
      </button>
      <div style={{ overflowY:"auto", flex:1, display:"flex", flexDirection:"column", gap:6, paddingRight:2 }}>
        {player.champions?.map(c=>{
          const active = selectedChampion===c.champion;
          return (
            <button key={c.champion} onClick={()=>setSelectedChampion(p=>p===c.champion?null:c.champion)}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:8, cursor:"pointer", textAlign:"left",
                background: active ? selectedTint : "transparent",
                border: active ? `1px solid rgba(${teamRgb},0.45)` : `1px solid ${C.border}`,
              }}
            >
              <img src={`https://ddragon.leagueoflegends.com/cdn/${ddVersion}/img/champion/${c.champion}.png`}
                alt={c.champion} style={{ width:36, height:36, borderRadius:6, objectFit:"cover", flexShrink:0 }}
                onError={e=>{e.target.src=`https://ddragon.leagueoflegends.com/cdn/img/champion/loading/${c.champion}_0.jpg`;}}
              />
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:"#f4f4f5", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{c.champion}</div>
                <div style={{ fontSize:11, color:C.muted }}>{c.games}g · {c.winrate}% WR</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ── Rendu ────────────────────────────────────────────────────────── */
  return (
    <div
      style={{ position:"fixed", inset:0, zIndex:50, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.78)", backdropFilter:"blur(6px)", padding:16 }}
      onClick={onClose}
    >
      <style>{`
        .scout-history-row {
          display: grid;
          grid-template-columns: 32px 54px minmax(120px, 1.1fr) minmax(130px, 0.9fr) minmax(150px, 1.1fr) minmax(120px, 0.8fr) 42px;
          align-items: center;
          gap: 12px;
        }
        @media (max-width: 860px) {
          .scout-history-row {
            grid-template-columns: 32px 48px minmax(140px, 1fr) minmax(96px, auto);
          }
          .scout-history-row .history-items,
          .scout-history-row .history-runes,
          .scout-history-row .history-economy {
            grid-column: 3 / -1;
          }
        }
      `}</style>
      <div
        style={{ background:cardBg, border:cardBorder, borderTop:cardBorderTop, borderRadius:14, width:"100%", maxWidth:1280, height:"92vh", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:`0 32px 80px rgba(0,0,0,0.72), 0 0 34px rgba(${teamRgb},0.08)`, fontFamily:"'Inter',system-ui,sans-serif" }}
        onClick={e=>e.stopPropagation()}
      >

        {/* ── HEADER ─────────────────────────────────────────────────── */}
        <div style={{ padding:"16px 24px", borderBottom:innerBorder, background:"transparent", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
          <div>
            <h2 style={{ margin:0, fontSize:22, fontWeight:800, color:"#f4f4f5" }}>
              {player.gameName} <span style={{ color:C.muted, fontSize:16, fontWeight:400 }}>#{player.tagLine}</span>
            </h2>
            <div style={{ fontSize:12, color:C.muted, marginTop:4 }}>
              <span style={{ color:accentColor, fontWeight:600 }}>{player.rank || "Unranked"}</span>
              <span style={{ margin:"0 6px" }}>·</span>
              <span>{player.gamesCount} games analysées</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background:"transparent", border:"none", color:C.muted, fontSize:28, lineHeight:1, cursor:"pointer", padding:"4px 8px", borderRadius:6 }}
            onMouseEnter={e=>e.currentTarget.style.color="#f4f4f5"} onMouseLeave={e=>e.currentTarget.style.color=C.muted}>
            ×
          </button>
        </div>

        {/* ── ONGLETS ────────────────────────────────────────────────── */}
        <div style={{ display:"flex", borderBottom:innerBorder, background:cardSurface, flexShrink:0 }}>
          {[["historique","Historique"],["impact","Analyse d'Impact"]].map(([id,label])=>(
            <button key={id} onClick={()=>{setActiveTab(id);if(id==="impact")setShowDetailedImpact(false);}}
              style={{ flex:1, padding:"12px 0", fontSize:13, fontWeight:600, cursor:"pointer", background:"transparent", border:"none",
                color: activeTab===id ? "#f4f4f5" : C.muted,
                borderBottom: activeTab===id ? `2px solid ${accentColor}` : "2px solid transparent",
              }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── CONTENU ────────────────────────────────────────────────── */}
        <div style={{ flex:1, overflowY:"auto", padding:24, background:"transparent", minHeight:0 }}>

          {/* ════════════════ HISTORIQUE ════════════════ */}
          {activeTab==="historique" && (
            <div style={{ maxWidth:820, margin:"0 auto" }}>

              {/* Barre résumé */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:20 }}>
                {[
                  { label:"Victoires / Défaites", value:`${wins}V · ${losses}D`, color: wins>=losses ? C.win : C.loss },
                  { label:"Winrate", value:`${wr}%`, color: wr>=50 ? C.win : C.loss },
                  { label:"KDA moyen", value:avgKda, color: kdaColor(Number(avgKda)) },
                  { label:"CS total", value:totalCs.toLocaleString(), color:C.text },
                ].map(({label,value,color})=>(
                  <div key={label} style={{ background:panelSurface, border:innerBorder, borderRadius:10, padding:"10px 14px" }}>
                    <div style={{ fontSize:10, color:C.muted, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:4 }}>{label}</div>
                    <div style={{ fontSize:18, fontWeight:800, color }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Liste des games */}
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {games.map((game,i)=>{
                  const d = Math.max(game.deaths,1);
                  const kda = ((game.kills+game.assists)/d).toFixed(2);
                  const isWin = game.win;
                  const isOpen = expandedGame === i;
                  const objectives = game.teamObjectives || {};
                  const enemyObjectives = game.enemyObjectives || {};
                  const allies = (game.participants || []).filter(p => p.teamId === game.teamId);
                  const enemies = (game.participants || []).filter(p => p.teamId !== game.teamId);
                  const renderParticipant = (p) => (
                    <div key={`${p.teamId}-${p.gameName}-${p.champion}`} style={{ display:"grid", gridTemplateColumns:"26px minmax(0,1fr) auto", gap:8, alignItems:"center", minWidth:0, padding:"5px 6px", borderRadius:7, background:cardSurface, border:`1px solid ${C.border}` }}>
                      <img src={`https://ddragon.leagueoflegends.com/cdn/${ddVersion}/img/champion/${p.champion}.png`} alt={p.champion} title={p.champion} style={{ width:26, height:26, borderRadius:5, objectFit:"cover" }} onError={e=>{e.currentTarget.style.visibility="hidden";}} />
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:11, color:"#f4f4f5", fontWeight:800, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.champion}</div>
                        <div style={{ fontSize:10, color:C.muted, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.gameName || "Joueur"}</div>
                      </div>
                      <div style={{ fontSize:10, color:C.muted, fontWeight:800, whiteSpace:"nowrap" }}>{p.kills}/{p.deaths}/{p.assists}</div>
                    </div>
                  );
                  return (
                    <div key={`new-${i}`} style={{ background:panelSurface, border:innerBorder, borderLeft: isWin ? `3px solid rgba(74,222,128,0.7)` : `3px solid rgba(248,113,113,0.7)`, borderRadius:12, overflow:"hidden" }}>
                      <div className="scout-history-row" style={{ padding:"11px 12px" }}>
                        <button onClick={() => setExpandedGame(isOpen ? null : i)} style={{ width:30, height:30, borderRadius:8, border:`1px solid ${C.borderSub}`, background:cardSurface, color:accentColor, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }} title={isOpen ? "Reduire la game" : "Voir la game complete"}>
                          {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                        </button>
                        <img src={`https://ddragon.leagueoflegends.com/cdn/${ddVersion}/img/champion/${game.champion}.png`} alt={game.champion} title={game.champion} style={{ width:48, height:48, borderRadius:10, objectFit:"cover", border:`1px solid ${C.border}` }} onError={e=>{e.target.src=`https://ddragon.leagueoflegends.com/cdn/img/champion/loading/${game.champion}_0.jpg`;}} />
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:14, fontWeight:900, color:"#f4f4f5", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{game.champion}</div>
                          <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap", marginTop:4 }}>
                            <span style={{ fontSize:10, fontWeight:900, color: isWin ? C.win : C.loss, background: isWin ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)", border:`1px solid ${isWin ? "rgba(74,222,128,0.25)" : "rgba(248,113,113,0.25)"}`, borderRadius:999, padding:"2px 7px" }}>{isWin ? "WIN" : "LOSE"}</span>
                            <span style={{ fontSize:11, color:C.muted }}>{formatDuration(game.gameDuration)}</span>
                            <span style={{ fontSize:11, color:C.muted }}>{game.lane || "ROLE"}</span>
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize:15, fontWeight:900, color:kdaColor(Number(kda)), whiteSpace:"nowrap" }}>{game.kills}/{game.deaths}/{game.assists}</div>
                          <div style={{ fontSize:10, color:C.muted, fontWeight:700 }}>{game.deaths===0 ? "Perfect KDA" : `${kda} KDA`}</div>
                        </div>
                        <div className="history-items" style={{ display:"flex", gap:4, alignItems:"center", minWidth:0, flexWrap:"wrap" }}>
                          {(game.items || []).slice(0,7).map((item, idx) => <SmallIcon key={`${item}-${idx}`} src={itemSrc(item)} title={`Item ${item}`} size={24} />)}
                          {Array.from({ length: Math.max(0, 6 - (game.items || []).length) }).map((_, idx) => <SmallIcon key={`empty-${idx}`} size={24} />)}
                        </div>
                        <div className="history-runes" style={{ display:"flex", gap:6, alignItems:"center", minWidth:0 }}>
                          {(game.summonerSpells || []).map((spellId, idx) => <SmallIcon key={`${spellId}-${idx}`} src={spellSrc(spellId)} title={`Summoner ${spellId}`} size={26} />)}
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontSize:11, color:"#f4f4f5", fontWeight:800, whiteSpace:"nowrap" }}>{RUNE_LABELS[game.runes?.primaryStyle] || "Runes"}</div>
                            <div style={{ fontSize:10, color:C.muted, whiteSpace:"nowrap" }}>Key {game.runes?.keystone || "-"}</div>
                          </div>
                        </div>
                        <div className="history-economy" style={{ textAlign:"right" }}>
                          <div style={{ fontSize:13, fontWeight:900, color:C.text }}>{game.cs_total||0} CS</div>
                          <div style={{ fontSize:10, color:C.muted }}>{formatGold(game.gold)} · lvl {game.level || "-"}</div>
                        </div>
                      </div>
                      {isOpen && (
                        <div style={{ borderTop:`1px solid ${C.border}`, padding:"12px", background:"rgba(0,0,0,0.18)" }}>
                          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(120px, 1fr))", gap:8, marginBottom:12 }}>
                            <StatPill label="Duree" value={formatDuration(game.gameDuration)} />
                            <StatPill label="Tours" value={`${objectives.towers || 0} - ${enemyObjectives.towers || 0}`} color={accentColor} />
                            <StatPill label="Drakes" value={`${objectives.dragons || 0} - ${enemyObjectives.dragons || 0}`} />
                            <StatPill label="Barons" value={`${objectives.barons || 0} - ${enemyObjectives.barons || 0}`} />
                            <StatPill label="Inhibs" value={`${objectives.inhibitors || 0} - ${enemyObjectives.inhibitors || 0}`} />
                            <StatPill label="Vision" value={game.visionScore || 0} />
                          </div>
                          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(260px, 1fr))", gap:10 }}>
                            <div style={{ minWidth:0 }}>
                              <div style={{ color:C.win, fontSize:10, fontWeight:900, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>Equipe joueur</div>
                              <div style={{ display:"grid", gap:5 }}>{allies.map(renderParticipant)}</div>
                            </div>
                            <div style={{ minWidth:0 }}>
                              <div style={{ color:C.loss, fontSize:10, fontWeight:900, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>Equipe adverse</div>
                              <div style={{ display:"grid", gap:5 }}>{enemies.map(renderParticipant)}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {false && <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {games.map((game,i)=>{
                  const d   = Math.max(game.deaths,1);
                  const kda = ((game.kills+game.assists)/d).toFixed(2);
                  const isWin = game.win;
                  return (
                    <div key={i} style={{
                      display:"grid", gridTemplateColumns:"44px 1fr auto auto",
                      alignItems:"center", gap:14,
                      padding:"10px 14px",
                      background: isWin ? "rgba(74,222,128,0.05)" : "rgba(248,113,113,0.05)",
                      border: innerBorder,
                      borderLeft: isWin ? `3px solid rgba(74,222,128,0.6)` : `3px solid rgba(248,113,113,0.6)`,
                      borderRadius:10,
                    }}>
                      {/* Image champion */}
                      <img
                        src={`https://ddragon.leagueoflegends.com/cdn/${ddVersion}/img/champion/${game.champion}.png`}
                        alt={game.champion}
                        title={game.champion}
                        style={{ width:44, height:44, borderRadius:8, objectFit:"cover" }}
                        onError={e=>{e.target.src=`https://ddragon.leagueoflegends.com/cdn/img/champion/loading/${game.champion}_0.jpg`;}}
                      />

                      {/* Nom + résultat */}
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color:"#f4f4f5" }}>{game.champion}</div>
                        <div style={{ fontSize:11, fontWeight:700, color: isWin ? C.win : C.loss, marginTop:2 }}>
                          {isWin ? "VICTOIRE" : "DÉFAITE"}
                        </div>
                      </div>

                      {/* KDA */}
                      <div style={{ textAlign:"center", minWidth:90 }}>
                        <div style={{ fontSize:14, fontWeight:700 }}>
                          <span style={{ color:C.text }}>{game.kills}</span>
                          <span style={{ color:C.muted, margin:"0 3px" }}>/</span>
                          <span style={{ color:C.loss }}>{game.deaths}</span>
                          <span style={{ color:C.muted, margin:"0 3px" }}>/</span>
                          <span style={{ color:C.text }}>{game.assists}</span>
                        </div>
                        <div style={{ fontSize:11, color:kdaColor(Number(kda)), fontWeight:600, marginTop:2 }}>
                          {game.deaths===0 ? "Parfait" : `${kda} KDA`}
                        </div>
                      </div>

                      {/* CS */}
                      <div style={{ textAlign:"right", minWidth:56 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{game.cs_total||0}</div>
                        <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>CS</div>
                      </div>
                    </div>
                  );
                })}
              </div>}
            </div>
          )}

          {/* ════════════════ IMPACT ════════════════ */}
          {activeTab==="impact" && (
            <div style={{ display:"flex", flexDirection:"column", gap:24, height:"100%" }}>

              {/* VUE RÉSUMÉ */}
              {!showDetailedImpact && (
                <div style={{ display:"flex", gap:20, width:"100%", maxWidth:1100, margin:"0 auto", height:500 }}>

                  {/* GAUCHE : filtre champion */}
                  <div style={{ width:220, flexShrink:0 }}>
                    <ChampionFilter />
                  </div>

                  {/* CENTRE : top 5 stats */}
                  <div style={{ flex:1, background:panelSurface, border:innerBorder, borderRadius:10, padding:18, overflowY:"auto" }}>
                    <div style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:14 }}>
                      {selectedChampion ? `Top 5 Impact — ${selectedChampion}` : "Top 5 Impact Global"}
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {topImpactStats.length>0 ? topImpactStats.map(([stat,val])=>{
                        const n = Number(val)||0;
                        const pos = n>=0;
                        const mag = Math.min(Math.abs(n),100);
                        const barColor = pos
                          ? `rgba(74,222,128,${0.3+0.7*(mag/100)})`
                          : `rgba(248,113,113,${0.3+0.7*(mag/100)})`;
                        return (
                          <div key={stat} style={{ background:cardSurface, border:`1px solid ${C.border}`, borderRadius:9, padding:"10px 14px" }}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                              <span style={{ fontSize:12, color:C.muted }}>{STAT_LABELS[stat]||stat}</span>
                              <span style={{ fontSize:12, fontWeight:700, color:pos?C.win:C.loss }}>{n>0?"+":""}{n.toFixed(1)}%</span>
                            </div>
                            <div style={{ width:"100%", height:4, background:C.surface2, borderRadius:999, overflow:"hidden" }}>
                              <div style={{ width:`${mag}%`, height:"100%", background:barColor, borderRadius:999, transition:"width 0.4s ease" }} />
                            </div>
                          </div>
                        );
                      }) : <p style={{ fontSize:12, color:C.muted }}>Aucune donnée disponible.</p>}
                    </div>
                  </div>

                  {/* DROITE : profil + bouton détails */}
                  <div style={{ width:280, flexShrink:0, display:"flex", flexDirection:"column", gap:16 }}>
                    {/* Profil dominant */}
                    <div style={{ flex:1, background:panelSurface, border:innerBorder, borderRadius:10, padding:20, display:"flex", flexDirection:"column", justifyContent:"center" }}>
                      {mainSection ? (
                        <>
                          <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14 }}>
                            <div style={{ fontSize:40, color:accentColor, background:selectedTint, padding:14, borderRadius:12, border:`1px solid rgba(${teamRgb},0.25)`, lineHeight:1, display:"flex" }}>
                              <mainSection.section.icon />
                            </div>
                            <div>
                              <div style={{ fontSize:10, color:C.muted, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>Tendances</div>
                              <div style={{ fontSize:20, fontWeight:800, color:"#f4f4f5" }}>{mainSection.section.label}</div>
                            </div>
                          </div>
                          <div style={{ fontSize:13, color:C.muted, lineHeight:1.6 }}>{mainSection.section.description}</div>
                        </>
                      ) : (
                        <div style={{ fontSize:13, color:C.muted, textAlign:"center" }}>Données insuffisantes pour dégager un profil.</div>
                      )}
                    </div>

                    {/* Bouton analyse experte */}
                    <button onClick={()=>setShowDetailedImpact(true)}
                      style={{ padding:"14px 0", borderRadius:10, border:`1px solid rgba(${teamRgb},0.45)`, background:selectedTint, color:accentColor, fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, transition:"background 0.15s" }}
                      onMouseEnter={e=>e.currentTarget.style.background=`rgba(${teamRgb},0.18)`}
                      onMouseLeave={e=>e.currentTarget.style.background=selectedTint}>
                      <FaChartBar /> Analyse experte détaillée
                    </button>
                  </div>
                </div>
              )}

              {/* VUE DÉTAILLÉE */}
              {showDetailedImpact && (
                <div style={{ display:"flex", flexDirection:"column", gap:16, height:"100%" }}>
                  <button onClick={()=>setShowDetailedImpact(false)}
                    style={{ display:"inline-flex", alignItems:"center", gap:6, background:"transparent", border:"none", color:C.muted, fontSize:13, cursor:"pointer", alignSelf:"flex-start" }}
                    onMouseEnter={e=>e.currentTarget.style.color="#f4f4f5"} onMouseLeave={e=>e.currentTarget.style.color=C.muted}>
                    <FaArrowLeft /> Retour au résumé
                  </button>

                  <div style={{ display:"flex", gap:20, flex:1, minHeight:0 }}>

                    {/* Colonne gauche : sous-onglets Champions / Statistiques */}
                    <div style={{ width:240, flexShrink:0, display:"flex", flexDirection:"column", gap:8, height:"100%", minHeight:0 }}>

                      {/* Switcher */}
                      <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                        {["champions", "statistiques"].map(tab => (
                          <button key={tab} onClick={()=>setLeftPanelTab(tab)} style={{
                            flex:1, padding:"5px 0", borderRadius:6, border:`1px solid ${leftPanelTab===tab ? `rgba(${teamRgb},0.45)` : C.borderSub}`,
                            background: leftPanelTab===tab ? selectedTint : "transparent",
                            color: leftPanelTab===tab ? accentColor : C.muted,
                            fontSize:11, fontWeight:700, cursor:"pointer", textTransform:"capitalize", transition:"all 0.15s"
                          }}>
                            {tab === "champions" ? "Champions" : "Statistiques"}
                          </button>
                        ))}
                      </div>

                      {/* Panneau actif — prend toute la hauteur restante */}
                      <div style={{ flex:1, minHeight:0, overflow:"hidden" }}>

                        {leftPanelTab === "champions" && (
                          <ChampionFilter height="100%" />
                        )}

                        {leftPanelTab === "statistiques" && (
                          <div style={{ background:panelSurface, border:innerBorder, borderRadius:10, padding:14, display:"flex", flexDirection:"column", height:"100%", minHeight:0 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10, flexShrink:0 }}>
                              Filtrer les statistiques
                            </div>
                            <div style={{ overflowY:"auto", flex:1, display:"flex", flexDirection:"column", gap:6, paddingRight:2, minHeight:0 }}>
                              {Object.entries(CATEGORIZED_STATS).map(([cat,stats])=>{
                                const avail = stats.filter(s=>Object.keys(currentImpact).includes(s));
                                if (!avail.length) return null;
                                const allOn = avail.every(s=>selectedStats.includes(s));
                                const someOn = avail.some(s=>selectedStats.includes(s));
                                return (
                                  <div key={cat} style={{ background:cardSurface, border:`1px solid ${C.border}`, borderRadius:8, overflow:"hidden" }}>
                                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 12px", borderBottom: expandedCats[cat] ? `1px solid ${C.border}` : "none" }}>
                                      <button onClick={()=>toggleCat(cat)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, fontWeight:700, color:accentColor, textAlign:"left", flex:1 }}>
                                        {cat} {expandedCats[cat] ? "▲" : "▼"}
                                      </button>
                                      <input type="checkbox" checked={allOn} ref={el=>{if(el)el.indeterminate=!allOn&&someOn;}} onChange={()=>toggleCatAll(avail)}
                                        style={{ cursor:"pointer", accentColor:accentColor }} />
                                    </div>
                                    {expandedCats[cat] && (
                                      <div style={{ padding:"8px 12px", display:"flex", flexDirection:"column", gap:6 }}>
                                        {avail.map(stat=>(
                                          <label key={stat} style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:11 }}>
                                            <input type="checkbox" checked={selectedStats.includes(stat)} onChange={()=>toggleStat(stat)} style={{ cursor:"pointer", accentColor:accentColor }} />
                                            <span style={{ color:C.text }}>{STAT_LABELS[stat]||stat}</span>
                                          </label>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                      </div>
                    </div>

                    {/* Colonne droite : graphique */}
                    <div style={{ flex:1, background:panelSurface, border:innerBorder, borderRadius:10, padding:20, display:"flex", flexDirection:"column", minHeight:500 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexShrink:0 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.07em" }}>
                          {selectedChampion ? `Détail impact — ${selectedChampion}` : "Détail impact — Global"}
                        </div>
                        <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:C.muted, cursor:"pointer" }}>
                          <input type="checkbox" checked={!sortDesc} onChange={()=>setSortDesc(p=>!p)} style={{ accentColor:accentColor, cursor:"pointer" }} />
                          Inverser le tri
                        </label>
                      </div>
                      <div style={{ flex:1, position:"relative", minHeight:400 }}>
                        {filteredData.length===0
                          ? <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, color:C.muted }}>Aucune statistique sélectionnée.</div>
                          : <div style={{ position:"absolute", inset:0 }}><ChartImpact data={filteredData} /></div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
